import { ApolloError } from "@apollo/client";
import { Box, Flex } from "@chakra-ui/react";
import React, { useCallback } from "react";

import { Spinner, useToast } from "../../../../components";
import { formatDetails } from "../../../../utils/call";
import { copy } from "../../../../utils/clipboard";
import { useSendGAEvent } from "../../../../utils/googleAnalytics";
import { formatSegment } from "../../../../utils/transcript";
import {
  AiInterviewCallFragment,
  AiInterviewStatus,
  CallNoteFragment,
  CallSpeakerOption,
  CallStatus,
  TranscriptSegmentFragment,
} from "../../../graphql";
import { ClipRange } from "../../../types";
import { MediaPlayerInterface } from "../../MediaPlayer/useMediaPlayer";
import { buildProgressMessage } from "../../Recording/Transcript/utils";
import Transcript from "../Transcript";

type TranscriptTabProps = {
  call: Pick<
    AiInterviewCallFragment,
    | "id"
    | "duration"
    | "canEdit"
    | "canShare"
    | "canShareExternal"
    | "clipsCount"
    | "position"
    | "speakers"
    | "transcriptLanguageCode"
    | "isProcessing"
    | "endTime"
    | "status"
    | "streamableAudio"
    | "aiInterview"
  >;
  transcript: TranscriptSegmentFragment[];
  loading: boolean;
  error?: ApolloError;
  notes: CallNoteFragment[];
  autoScrollEnabled: boolean;
  player: Pick<
    MediaPlayerInterface,
    "playing" | "time" | "seek" | "play" | "duration"
  >;
  setAutoScrollEnabled(enabled: boolean): void;
  onCreateClipClick?(clipRange: ClipRange): void;
  speakerOptions?: Pick<CallSpeakerOption, "id" | "label">[];
};

const TranscriptTab: React.FC<TranscriptTabProps> = ({
  call,
  transcript,
  loading,
  error,
  autoScrollEnabled,
  player,
  setAutoScrollEnabled,
  onCreateClipClick,
  speakerOptions,
}) => {
  const sendGAEvent = useSendGAEvent();
  const toast = useToast();

  const onCopyTranscript = useCallback((): void => {
    if (!call) {
      return;
    }
    sendGAEvent("copy_transcript", "call_review");
    const callDetails = formatDetails(call);
    const speakerLabels = call.speakers.map((speaker) => speaker.label);
    const transcriptText =
      transcript
        ?.map((segment) => formatSegment(speakerLabels, segment))
        .reduce((previous, current) => `${previous}\n\n${current}`, "") ?? "";
    const text = `${callDetails}\n\n${transcriptText}`;
    copy(text);
    toast({
      title: "Transcript copied to clipboard",
      status: "success",
    });
  }, [call, transcript, toast]);

  // Show error message if AI interview failed
  if (call.aiInterview?.status === AiInterviewStatus.Error) {
    return (
      <Flex flexDirection="column" align="center" py="10">
        <Box
          fontWeight="bold"
          color="red.600"
          fontSize="lg"
          textAlign="center"
          marginTop={3}
        >
          Interview Failed
        </Box>
        <Box fontSize="md" fontWeight="400" mt={2} px={6} textAlign="center">
          The AI interviewer encountered an error during this interview. The
          recording may be incomplete or unavailable.
        </Box>
      </Flex>
    );
  }

  if (call.isProcessing || call.status === CallStatus.InProgress) {
    let recordingMessage;
    let hasRecording = false;
    if (call.status !== CallStatus.InProgress) {
      hasRecording = !!call.streamableAudio;
      recordingMessage = hasRecording
        ? "Transcribing recording..."
        : "Processing and transcribing recording…";
    } else {
      recordingMessage = "Interview is in progress...";
    }

    return (
      <Flex flexDirection="column" align="center" py="10">
        <Spinner width="48px" height="48px" />
        <Box
          fontWeight="bold"
          color="gray.800"
          fontSize="lg"
          textAlign="center"
          marginTop={3}
        >
          {recordingMessage}
          {call.duration && (
            <Box fontSize="md" fontWeight="400" mt={2} px={6}>
              {buildProgressMessage(call.endTime)}
            </Box>
          )}
          {hasRecording && (
            <Box width="100%" fontSize="lg" my={6} textAlign="center">
              {
                'Click "Play" to start listening while we finish transcribing the interview.'
              }
            </Box>
          )}
          {call.status === CallStatus.InProgress && (
            <Box fontSize="md" fontWeight="400" mt={2} px={6}>
              When the interview is completed and processed, you can play back
              the recording and review the transcript.
            </Box>
          )}
        </Box>
      </Flex>
    );
  }

  return (
    <Transcript
      h="100%"
      callId={call.id}
      transcript={transcript}
      autoScrollEnabled={autoScrollEnabled}
      speakers={call.speakers}
      player={player}
      setAutoScrollEnabled={setAutoScrollEnabled}
      onCreateClipClick={onCreateClipClick}
      onCopyTranscript={onCopyTranscript}
      speakerOptions={speakerOptions}
      canChangeSpeaker={call.canEdit}
      isVideoVisible={false}
      isVideoCall={false}
      loading={loading}
      error={error}
    />
  );
};

export default TranscriptTab;
